<template>
  <div class="summary-container">
    <h3 class="component-title">Booking Overview</h3>

    <div class="schedule-compact-row">
      <div 
        class="schedule-tab" 
        :class="{ 'is-clickable': passesCountToday > 0 }"
        @click="passesCountToday > 0 ? showTicketsModal('today') : null"
      >
        <div class="status-indicator">
          <span v-if="loading" class="spinner"></span>
          <span v-else class="dot" :class="passesCountToday > 0 ? 'active' : 'empty'"></span>
        </div>
        
        <div class="tab-meta">
          <p class="tab-label">Today</p>
          <template v-if="loading">
            <b-skeleton size="is-small" width="40px"></b-skeleton>
          </template>
          <template v-else>
            <p class="tab-status" :class="{ 'has-tickets': passesCountToday > 0 }">
              {{ passesCountToday === 0 ? 'No tickets sold' : passesCountToday + ' Tickets sold' }}
              <b-icon v-if="passesCountToday > 0" pack="fas" icon="arrow-right" size="is-small"></b-icon>
            </p>
          </template>
        </div>
      </div>

      <div class="tab-divider"></div>

      <div 
        class="schedule-tab" 
        :class="{ 'is-clickable': passesCountTomorrow > 0 }"
        @click="passesCountTomorrow > 0 ? showTicketsModal('tomorrow') : null"
      >
        <div class="status-indicator">
          <span v-if="loading" class="spinner"></span>
          <span v-else class="dot" :class="passesCountTomorrow > 0 ? 'active' : 'empty'"></span>
        </div>

        <div class="tab-meta">
          <p class="tab-label">Tomorrow</p>
          <template v-if="loading">
            <b-skeleton size="is-small" width="40px"></b-skeleton>
          </template>
          <template v-else>
            <p class="tab-status" :class="{ 'has-tickets': passesCountTomorrow > 0 }">
              {{ passesCountTomorrow === 0 ? 'No tickets sold' : passesCountTomorrow + ' Tickets sold' }}
              <b-icon v-if="passesCountTomorrow > 0" pack="fas" icon="arrow-right" size="is-small"></b-icon>
            </p>
          </template>
        </div>
      </div>
    </div>

    <b-modal 
      v-model="isModalActive.today" 
      :width="900" 
      scroll="keep" 
      aria-modal
      full-screen
      class="professional-modal"
    >
      <div class="modal-card-custom">
        <header class="modal-custom-header">
          <div class="header-content is-align-items-flex-start is-flex-direction-column">
            <p class="modal-badge">{{ passesCountToday }} Tickets</p>
            <p class="modal-title">Today's Schedule</p>
          </div>
        
        </header>
        <section class="modal-custom-body">
          <BoughtPassesTable :tickets="todayTickets" :loading="loading" />
        </section>
      </div>
    </b-modal>

    <b-modal 
      v-model="isModalActive.tomorrow" 
      :width="900" 
      scroll="keep" 
      aria-modal
      full-screen
      class="professional-modal"
    >
      <div class="modal-card-custom">
        <header class="modal-custom-header">
          <div class="header-content is-align-items-flex-start is-flex-direction-column">
            <p class="modal-badge">{{ passesCountTomorrow }} Tickets</p>
            <p class="modal-title">Tomorrow's Schedule</p>
          </div>
        </header>
        <section class="modal-custom-body">
          <BoughtPassesTable :tickets="tomorrowTickets" :loading="loading" />
        </section>
      </div>
    </b-modal>
  </div>
</template>

<script>
import BoughtPassesTable from "./BoughtPassesTable.vue";
import { db } from "../../firebase";

export default {
  name: "BookingStatusOverview",
  components: { BoughtPassesTable },
  props: {
    isAdmin: { type: Boolean, default: true },
  },
  data() {
    return {
      isModalActive: { today: false, tomorrow: false },
      passesCountToday: 0,
      passesCountTomorrow: 0,
      loading: true,
      todayTickets: [],
      tomorrowTickets: [],
    };
  },
  created() {
    this.getTicketsCount();
  },
  methods: {
    showTicketsModal(type) {
      this.isModalActive[type] = true;
    },
    async getTicketsCount() {
      this.loading = true;
      const dbRef = db.firestore();
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const todayMidnight = now.getTime();
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowMidnight = tomorrow.getTime();
      const dayAfterTomorrow = new Date(tomorrow);
      dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 1);
      const dayAfterTomorrowMidnight = dayAfterTomorrow.getTime();

      try {
        const [todaySnapshot, tomorrowSnapshot] = await Promise.all([
          dbRef.collection("passes")
            .where("expectedDateOfUse", ">=", todayMidnight.toString())
            .where("expectedDateOfUse", "<", tomorrowMidnight.toString()).get(),
          dbRef.collection("passes")
            .where("expectedDateOfUse", ">=", tomorrowMidnight.toString())
            .where("expectedDateOfUse", "<", dayAfterTomorrowMidnight.toString()).get()
        ]);

        // Admin test purchases aren't sales
        const sold = (snapshot) => snapshot.docs.filter(doc => !doc.data().isTest);
        this.passesCountToday = sold(todaySnapshot).reduce((sum, doc) => sum + (doc.data().numberOfTickets || 0), 0);
        this.passesCountTomorrow = sold(tomorrowSnapshot).reduce((sum, doc) => sum + (doc.data().numberOfTickets || 0), 0);

        this.todayTickets = sold(todaySnapshot).map(doc => ({ id: doc.id, ...doc.data() }));
        this.tomorrowTickets = sold(tomorrowSnapshot).map(doc => ({ id: doc.id, ...doc.data() }));
      } catch (error) {
        console.error("Error fetching tickets:", error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
// Container & Tabs
.summary-container {
  margin-bottom: 24px;
}

.component-title {
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  margin-bottom: 12px;
  padding-left: 4px;
}

.schedule-compact-row {
  display: flex;
  align-items: center;
  background: #ffffff;
  padding: 12px 20px;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  .schedule-tab {
    display: flex;
    align-items: center;
    gap: 16px;
    flex: 1;
    padding: 10px;
    border-radius: 12px;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

    &.is-clickable {
      cursor: pointer;
      &:hover { 
        background: #f8fafc; 
        transform: translateY(-1px);
      }
    }

    .dot {
      height: 10px;
      width: 10px;
      border-radius: 50%;
      &.active { 
        background: #2C6E57;
        box-shadow: 0 0 0 4px rgba(44, 110, 87, 0.15);
      }
      &.empty { background: #cbd5e1; }
    }

    .tab-meta {
      .tab-label {
        font-size: 0.75rem;
        font-weight: 600;
        color: #94a3b8;
        margin-bottom: 2px;
      }
      .tab-status {
        font-size: 0.95rem;
        font-weight: 600;
        color: #1e293b;
        display: flex;
        align-items: center;
        gap: 8px;

        .icon {
          color: #2C6E57;
          transition: transform 0.2s ease;
        }
      }
    }
    
    &:hover .icon {
      transform: translateX(3px);
    }
  }

  .tab-divider {
    width: 1px;
    height: 40px;
    background: #f1f5f9;
    margin: 0 20px;
  }
}

// Phones: Today and Tomorrow stacked, so "2 tickets sold" stays on one line
@media (max-width: 600px) {
  .schedule-compact-row {
    flex-direction: column;
    align-items: stretch;
    padding: 6px 10px;

    .schedule-tab {
      gap: 12px;
    }

    .tab-divider {
      width: 100%;
      height: 1px;
      margin: 2px 0;
    }
  }
}

// Professional Modal Styling
.modal-card-custom {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

.modal-custom-header {
  padding: 24px 32px;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .header-content {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .modal-title {
    font-size: 1.25rem;
    font-weight: 700;
    color: #0f172a;
  }

  .modal-badge {
    background: #eff6ff;
    color: #1e40af;
    font-size: 0.75rem;
    font-weight: 700;
    padding: 4px 12px;
    border-radius: 999px;
    border: 1px solid #dbeafe;
  }

  .close-btn {
    background: #f1f5f9;
    border: none;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #64748b;
    transition: all 0.2s ease;

    &:hover {
      background: #e2e8f0;
      color: #0f172a;
    }
  }
}

.modal-custom-body {
  padding: 0; // Let the table handle its own internal padding
  overflow-y: auto;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>